import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Audio tracks for the drift world
const DRIFT_TRACKS = [
  'https://customer-assets.emergentagent.com/job_579aa3c7-027e-4ad0-be92-a2c2303c3276/artifacts/f6cun90q_guitar-gabber-jamblu.wav',
  'https://customer-assets.emergentagent.com/job_579aa3c7-027e-4ad0-be92-a2c2303c3276/artifacts/s51xdtld_jamblu%20-%20piece%20of%20shit%20ra%28beat-post%20d80%20jam-at%20home%29%20onra%20can%20suck%20my%20dick.wav',
  'https://customer-assets.emergentagent.com/job_579aa3c7-027e-4ad0-be92-a2c2303c3276/artifacts/kri05qyn_final%20Everythingeverywhereallatonce%20-16bit-sp404.wav'
];

// Constellation patterns (relative positions)
const CONSTELLATIONS = [
  { name: 'orion', stars: [[0,0], [20,10], [40,5], [30,30], [25,50], [35,50], [20,70], [40,70]] },
  { name: 'dipper', stars: [[0,0], [20,5], [40,0], [60,10], [55,30], [35,35], [15,30]] },
  { name: 'cassiopeia', stars: [[0,20], [20,0], [40,15], [60,0], [80,20]] },
  { name: 'cygnus', stars: [[40,0], [40,30], [40,60], [20,40], [60,40]] },
  { name: 'lyra', stars: [[30,0], [0,20], [60,20], [10,50], [50,50]] },
];

// Shimmer sound
const createShimmerSound = (audioContext) => {
  const duration = 1.5;
  const oscillator1 = audioContext.createOscillator();
  const oscillator2 = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator1.type = 'sine';
  oscillator1.frequency.setValueAtTime(800, audioContext.currentTime);
  oscillator1.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.3);
  oscillator1.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + duration);
  
  oscillator2.type = 'sine';
  oscillator2.frequency.setValueAtTime(1200, audioContext.currentTime);
  oscillator2.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + duration);
  
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.1);
  gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);
  
  oscillator1.connect(gainNode);
  oscillator2.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator1.start();
  oscillator2.start();
  oscillator1.stop(audioContext.currentTime + duration);
  oscillator2.stop(audioContext.currentTime + duration);
};

const ExperimentsPage = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('entering');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showDriftingText, setShowDriftingText] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const [activeConstellation, setActiveConstellation] = useState(null);
  const [distantObject, setDistantObject] = useState(null);
  const [stars, setStars] = useState([]);
  const audioRef = useRef(null);
  const containerRef = useRef(null);

  // Generate drifting stars
  useEffect(() => {
    const generateStars = () => {
      return Array.from({ length: 150 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 2 + 0.5,
        speed: Math.random() * 0.5 + 0.2,
        delay: Math.random() * 5,
        brightness: Math.random() * 0.5 + 0.5
      }));
    };
    setStars(generateStars());
  }, []);

  // Show constellations periodically
  useEffect(() => {
    if (phase !== 'active') return;
    
    const showConstellation = () => {
      const randomConstellation = CONSTELLATIONS[Math.floor(Math.random() * CONSTELLATIONS.length)];
      const position = {
        x: Math.random() * 60 + 20,
        y: Math.random() * 60 + 20,
        scale: Math.random() * 0.5 + 0.5
      };
      setActiveConstellation({ ...randomConstellation, position });
      
      // Hide after 8 seconds
      setTimeout(() => {
        setActiveConstellation(null);
      }, 8000);
    };
    
    // First constellation after 5 seconds
    const firstTimer = setTimeout(showConstellation, 5000);
    
    // Then every 15-25 seconds
    const interval = setInterval(() => {
      if (Math.random() > 0.3) { // 70% chance
        showConstellation();
      }
    }, 18000);
    
    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [phase]);

  // Show distant objects (galaxies, solar systems) periodically
  useEffect(() => {
    if (phase !== 'active') return;
    
    const showDistantObject = () => {
      const type = Math.random() > 0.5 ? 'galaxy' : 'solar-system';
      const position = {
        x: Math.random() * 80 + 10,
        y: Math.random() * 80 + 10
      };
      setDistantObject({ type, position });
      
      // Fade out after 12 seconds
      setTimeout(() => {
        setDistantObject(null);
      }, 12000);
    };
    
    // First object after 10 seconds
    const firstTimer = setTimeout(showDistantObject, 10000);
    
    // Then every 20-30 seconds
    const interval = setInterval(() => {
      if (Math.random() > 0.4) { // 60% chance
        showDistantObject();
      }
    }, 25000);
    
    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [phase]);

  // Handle mouse movement for parallax
  const handleMouseMove = useCallback((e) => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    setMousePos({
      x: (e.clientX - centerX) / centerX * 20,
      y: (e.clientY - centerY) / centerY * 15
    });
  }, []);

  // Entry animation
  useEffect(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      createShimmerSound(audioContext);
    } catch (e) {
      console.log('Audio context not available');
    }

    const musicTimer = setTimeout(() => {
      if (audioRef.current) {
        const randomTrack = DRIFT_TRACKS[Math.floor(Math.random() * DRIFT_TRACKS.length)];
        audioRef.current.src = randomTrack;
        audioRef.current.volume = 0;
        audioRef.current.play().catch(() => {});
        
        let vol = 0;
        const fadeIn = setInterval(() => {
          vol += 0.02;
          if (vol >= 0.6) {
            vol = 0.6;
            clearInterval(fadeIn);
          }
          if (audioRef.current) audioRef.current.volume = vol;
        }, 50);
      }
      setPhase('active');
    }, 1500);

    // Show "drifting..." text periodically (every 45-60 seconds)
    const showDriftingSequence = () => {
      setShowDriftingText(true);
      
      // After 4 seconds, hide "drifting..." and show "..."
      setTimeout(() => {
        setShowDriftingText(false);
        setShowEllipsis(true);
        
        // After 7 seconds, hide "..."
        setTimeout(() => {
          setShowEllipsis(false);
        }, 7000);
      }, 4000);
    };

    // First "drifting..." after 8 seconds
    const firstDriftTimer = setTimeout(showDriftingSequence, 8000);
    
    // Then repeat every 45-60 seconds
    const driftInterval = setInterval(() => {
      showDriftingSequence();
    }, 50000); // 50 seconds

    return () => {
      clearTimeout(musicTimer);
      clearTimeout(firstDriftTimer);
      clearInterval(driftInterval);
    };
  }, []);

  // Handle exit
  const handleExit = useCallback(() => {
    setPhase('exiting');
    
    if (audioRef.current) {
      let vol = audioRef.current.volume;
      const fadeOut = setInterval(() => {
        vol -= 0.05;
        if (vol <= 0) {
          clearInterval(fadeOut);
          if (audioRef.current) audioRef.current.pause();
        }
        if (audioRef.current) audioRef.current.volume = Math.max(0, vol);
      }, 50);
    }
    
    setTimeout(() => navigate('/'), 1500);
  }, [navigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleExit();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleExit, handleMouseMove]);

  return (
    <div 
      ref={containerRef}
      className={`space-drift ${phase}`}
    >
      <audio ref={audioRef} loop />
      
      {/* Drift back button - top left, translucent */}
      <button 
        className="drift-back-btn"
        onClick={handleExit}
        aria-label="Drift back"
        data-testid="drift-close-btn"
      >
        drift back
      </button>
      
      {/* Black space background */}
      <div className="space-background" />
      
      {/* Drifting stars - flying through space effect */}
      <div 
        className="space-stars"
        style={{ transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)` }}
      >
        {stars.map(star => (
          <div
            key={star.id}
            className="space-star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.brightness,
              animationDuration: `${3 / star.speed}s`,
              animationDelay: `${star.delay}s`
            }}
          />
        ))}
      </div>

      {/* Streaking stars (warp effect) */}
      <div className="warp-stars">
        {Array.from({ length: 30 }, (_, i) => (
          <div
            key={i}
            className="warp-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${Math.random() * 2 + 1}s`
            }}
          />
        ))}
      </div>

      {/* Active constellation */}
      {activeConstellation && (
        <svg 
          className="constellation"
          style={{
            left: `${activeConstellation.position.x}%`,
            top: `${activeConstellation.position.y}%`,
            transform: `translate(-50%, -50%) scale(${activeConstellation.position.scale})`,
          }}
          viewBox="0 0 100 100"
          width="200"
          height="200"
        >
          {/* Connection lines */}
          {activeConstellation.stars.slice(1).map((star, i) => (
            <line
              key={`line-${i}`}
              x1={activeConstellation.stars[i][0]}
              y1={activeConstellation.stars[i][1]}
              x2={star[0]}
              y2={star[1]}
              className="constellation-line"
            />
          ))}
          {/* Stars */}
          {activeConstellation.stars.map((star, i) => (
            <circle
              key={`star-${i}`}
              cx={star[0]}
              cy={star[1]}
              r="2"
              className="constellation-star"
            />
          ))}
        </svg>
      )}

      {/* Distant galaxy */}
      {distantObject?.type === 'galaxy' && (
        <div 
          className="distant-galaxy"
          style={{
            left: `${distantObject.position.x}%`,
            top: `${distantObject.position.y}%`,
          }}
        >
          <div className="galaxy-core" />
          <div className="galaxy-arm galaxy-arm-1" />
          <div className="galaxy-arm galaxy-arm-2" />
          <div className="galaxy-glow" />
        </div>
      )}

      {/* Distant solar system */}
      {distantObject?.type === 'solar-system' && (
        <div 
          className="distant-solar-system"
          style={{
            left: `${distantObject.position.x}%`,
            top: `${distantObject.position.y}%`,
          }}
        >
          <div className="solar-star" />
          <div className="orbit orbit-1"><div className="planet planet-1" /></div>
          <div className="orbit orbit-2"><div className="planet planet-2" /></div>
          <div className="orbit orbit-3"><div className="planet planet-3" /></div>
        </div>
      )}

      {/* Nebula clouds drifting by occasionally */}
      <div className="nebula-layer" style={{ transform: `translate(${mousePos.x * -0.2}px, ${mousePos.y * -0.2}px)` }} />
      
      {/* "drifting..." text */}
      {showDriftingText && (
        <div className="drift-text drifting-text">drifting...</div>
      )}
      
      {/* "..." ellipsis after drifting fades */}
      {showEllipsis && (
        <div className="drift-text ellipsis-text">...</div>
      )}
      
      {/* Transition overlay */}
      <div className={`drift-transition-overlay ${phase}`} />
    </div>
  );
};

export default ExperimentsPage;
